import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSave, faLink } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

function EditAccountModal({ account, onClose, onAccountUpdated }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    currency: "UAH",
    type: "card",
    holder: "Serhii",
    monobankAccountId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name || "",
        currency: account.currency || "UAH",
        type: account.type || "card",
        holder: account.holder || "Serhii",
        monobankAccountId: account.monobankAccountId || "",
      });
    }
  }, [account]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/accounts/${account.id}`,
        formData
      );

      if (onAccountUpdated) {
        onAccountUpdated();
      }
      onClose();
    } catch (error) {
      console.error("Error updating account:", error);
      setError(error.response?.data?.error || "Failed to update account");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!account) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">
            {t("editAccount", "Редагувати рахунок")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="name"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              {t("accountName", "Назва рахунку")}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-dark-bg border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
              required
            />
          </div>

          <div>
            <label
              htmlFor="currency"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              {t("currency", "Валюта")}
            </label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-dark-bg border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
              required
            >
              <option value="UAH">UAH</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="HUF">HUF</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="type"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              {t("type", "Тип")}
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-dark-bg border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
              required
            >
              <option value="card">{t("typeCard", "Картка")}</option>
              <option value="cash">{t("typeCash", "Готівка")}</option>
              <option value="bank">{t("typeBank", "Банк")}</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="holder"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              {t("holder", "Власник")}
            </label>
            <select
              id="holder"
              name="holder"
              value={formData.holder}
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-dark-bg border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
              required
            >
              <option value="Serhii">{t("holderSerhii", "Сергій")}</option>
              <option value="Kateryna">{t("holderKateryna", "Катерина")}</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="monobankAccountId"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faLink} className="text-primary-500" />
              {t("monobankAccountId", "ID рахунку Monobank")}
            </label>
            <input
              type="text"
              id="monobankAccountId"
              name="monobankAccountId"
              value={formData.monobankAccountId}
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-dark-bg border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white font-mono text-sm"
              placeholder="наприклад, ljELLwS5iWFnnrNJxrFBHw"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("monobankAccountIdHelp", "Залиште порожнім, щоб від'єднати від Monobank")}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t("cancel", "Скасувати")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 btn-primary rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  {t("save", "Зберегти")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default EditAccountModal;
